import {Component, createRef, RefObject} from "react";
import LazyImage from "./components/LazyImage";
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
// import autoAnimate from "@formkit/auto-animate";
// import Box from "./components/Box";
// import Truck from "./components/Truck";
// import Close from "./components/Close";
// import Facebook from "./components/Facebook";
// import Twitter from "./components/Twitter";
// import Pinterest from "./components/Pinterest";
import Colors from './colors.json';
import Drawer from "./components/Drawer";
import CFContentText from "./components/CFContentText";

declare global {
    interface Window {
        productPageData: IProductPageData;
        languages : IlanguagesData;
    }
}

interface IlanguagesData {
    productFinalSaleText: string;
}

interface IProductPageData {
    translations: {
        profitFromCard: string;
        signIn: string;
        addToCart: string;
    };
    card: {
        inCart: boolean;
        productId: number;
        variantId: number;
        name: string;
        discount: number;
        price: number;
    };
    customer: {
        loggedIn: boolean;
        isPrivileged: boolean;
        isAdmin: boolean;
    };
    settings: IProductPageSettings;
    data: IProductPageDataContent;
}

interface IProductPageSettings {
    enableHistoryState: boolean;
    showInventoryQuantity: boolean;
    showSku: boolean;
    stackProductImages: boolean;
    showThumbnails: boolean;
    enableVideoLooping: boolean;
    inventoryQuantityThreshold: number;
    showPriceInButton: boolean;
    enableImageZoom: boolean;
    showPaymentButton: boolean;
    useAjaxCart: boolean;
    finalSaleThreshold: number;
}

interface IProductPageDataContent {
    product: IProduct;
    firstSelectedVariant: Omit<IProductVariant, 'variantImageIds' | 'swatchImageId'>;
    options: {
        option1: IProductPageDataOptionsOption;
        option2: IProductPageDataOptionsOption;
        option3: IProductPageDataOptionsOption;
    };
    variants: IProductVariant[];
    medias: IProductMedia[];
    reorder?: number[];
    links: {
        sizeChart: {
            title: string;
            url: string;
        }
    }
    policies: {
        refund: string;
        shipping: string;
    };
}

interface IProduct {
    id: number;
    title: string;
    vendor: string;
    sku: string;
    handle: string;
    url: string;
    byVero: boolean;
    availableSoon: boolean;
    price: string;
    compare_at_price: string;
    description: string;
    composition: string;
    fit: string;
    length: string;
    shape: string;
    instructions: string;
}

interface IProductVariant {
    id: number;
    sku: string;
    option1: string;
    option2: string;
    option3: string;
    available: boolean;
    price: string;
    compare_at_price: string;
    variantImageIds?: number[];
    swatchImageId?: number;
}

interface IProductVariantResolved extends Omit<IProductVariant, 'variantImageIds' | 'swatchImageId'> {
    variantImages: IProductMedia[];
    swatchImage?: IProductMedia;
    order?: number;
}

interface IProductPageDataOptionsOption {
    name: string;
    values: string[];
}

export interface IProductMedia {
    id: number;
    alt: string;
    aspectRatio: number;
    swatchSrc: string;
    previewSrc: string;
    src: string;
}

interface ISelectedOptions {
    option1?: string;
    option2?: string;
}

export default class ProductPage extends Component {
    private readonly languages: IlanguagesData = window.languages;
    private readonly internalData: IProductPageData = window.productPageData;
    private readonly currentLanguage = document.documentElement.lang;
    private defaultVariantPerColor: IProductVariantResolved[] = [];
    private selectedOptions: ISelectedOptions = {};
    private firstPicture: RefObject<any> = createRef();
    // @ts-ignore
    private sidebarCart: HTMLElement | null = null;

    state = {
        currentVariant: this.internalData.data.firstSelectedVariant,
        currentThumbnail: 0,
        cardInCart: this.internalData.card.inCart,
        isPrivileged: this.internalData.customer.isPrivileged,
        adminError: [] as string[],
        policy: {
            opened: false as boolean,
            type: '' as 'refund' | 'shipping'
        }
    }
    private currentVariantFound: boolean;
    private _defaultVariantPerColor: IProductVariant[] = [];

    constructor(props: any) {
        super(props);

        // Check if the description is malformed (tags with empty spaces for example) or escaped
        this.internalData.data.product.description = this.safelyBuildHTML(this.internalData.data.product.description);

        // Build the HTMl for the policies
        this.internalData.data.policies.refund = this.safelyBuildHTML(this.internalData.data.policies.refund);
        this.internalData.data.policies.shipping = this.safelyBuildHTML(this.internalData.data.policies.shipping);

        this.currentVariantFound = true;
        // Getting the first variant of each color
        this.internalData.data.options.option1.values.forEach((value) => {
            // Find the variant that matches the condition
            const foundVariant = this.internalData.data.variants.find((variant) => variant.option1 === value);

            // Conditionally add the found variant to the array if it exists
            if (foundVariant) {
                this._defaultVariantPerColor = [...this._defaultVariantPerColor, foundVariant];
            } else if (!foundVariant && this.state.currentVariant.option1 === value) {
                this.currentVariantFound = false;
            }
        });

        if (this.internalData.data.options.option1.values.length > 1) {
            // Rebuilding the first variant of each color with the resolved images
            this._defaultVariantPerColor.forEach((variant) => {
                const variantImages = this.internalData.data.medias.filter((media) => variant.variantImageIds!.includes(media.id));
                const swatchImage = this.internalData.data.medias.find((media) => media.id === variant.swatchImageId)!;

                delete variant.variantImageIds;
                delete variant.swatchImageId;

                this.defaultVariantPerColor = [...this.defaultVariantPerColor, {
                    ...variant,
                    variantImages,
                    swatchImage
                }];
            });
        } else {
            const variant = this.internalData.data.variants[0];
            const variantImages = this.internalData.data.medias.map((media) => {
                return {
                    ...media,
                    id: this.internalData.data.medias.indexOf(media)
                }
            });

            if (variant?.swatchImageId) {
                const swatchImage = this.internalData.data.medias.find((media) => media.id === variant.swatchImageId)!;

                this.defaultVariantPerColor = [{
                    ...this.internalData.data.firstSelectedVariant,
                    variantImages,
                    swatchImage
                }];
            } else {
                this.defaultVariantPerColor = [{
                    ...this.internalData.data.firstSelectedVariant,
                    variantImages
                }];
            }
        }

        if (!this.internalData.data.firstSelectedVariant.available) {
            const firstAvailableVariantInSize = this.internalData.data.variants.find((variant) => variant.option1 === this.internalData.data.firstSelectedVariant.option1 && variant.available);
            this.state.currentVariant = firstAvailableVariantInSize || this.internalData.data.firstSelectedVariant;
            if (firstAvailableVariantInSize) {
                if (this.internalData.settings.enableHistoryState) {
                    const url = new URL(window.location.href);
                    url.searchParams.set('variant', firstAvailableVariantInSize.id.toString());
                    window.history.replaceState({ }, '', url.href);
                }
            }
        }

        this.checkReordering();

        this.sidebarCart = document.querySelector('#sidebar-cart');
    }

    componentDidMount() {
        this.selectedOptions = {
            option1: this.internalData.data.firstSelectedVariant.option1,
            option2: this.internalData.data.firstSelectedVariant.option2
        }

        if (!this.currentVariantFound && this._defaultVariantPerColor[0]) {
            this.selectedOptions = {
                option1: this._defaultVariantPerColor[0].option1,
                option2: this._defaultVariantPerColor[0].option2
            }
            this.changeVariant(1, this._defaultVariantPerColor[0].option1);
        }
    }

    private safelyBuildHTML = (html: string) => {
        // Create a temporary element to parse the HTML and get the textContent
        let tempElem = document.createElement('div');
        tempElem.innerHTML = html;
        return tempElem.textContent!
            .replace(/<([^>]+)>&nbsp;<\/\1>/g, '')
            .replace(/<([^>]+)>\s*<\/\1>/g, '')
            .replace(/<([^>]+)><br><\/\1>/g, '');
    }

    private addError = (message: string) => {
        this.state.adminError = [...this.state.adminError, message];
    }

    private checkReordering = () => {
        try {
            if (this.internalData.data.reorder) {
                let hasError = false;
                // Check if the reorder has two time the same index
                const reorderSet = new Set(this.internalData.data.reorder);
                if (reorderSet.size !== this.internalData.data.reorder.length) {
                    this.addError(`Le reorder est mal formé, il ne doit pas contenir deux fois le même index.<br />-> ${JSON.stringify(this.internalData.data.reorder)}`);
                    hasError = true;
                }

                // Check if we have the same amount of reorder and defaultVariantPerColor
                if (this.internalData.data.reorder.length !== this.defaultVariantPerColor.length) {
                    this.addError(`Le reorder est mal formé, il doit contenir le même nombre d'order que de couleur.<br /> -> (${this.internalData.data.reorder.length}/${this.defaultVariantPerColor.length})`);
                    hasError = true;
                }

                if (hasError) {
                    return;
                }

                this.internalData.data.reorder.forEach((defaultVariantIndex: number, newOrder: number) => {
                    this.defaultVariantPerColor[defaultVariantIndex - 1].order = newOrder;
                });
                this.defaultVariantPerColor.sort((a, b) => a.order! - b.order!);
            }
        } catch (e : any) {
            this.addError(`Erreur lors de la vérification du reorder: ${e.message}`);
        }
    }

    private intlMoney = new Intl.NumberFormat(this.currentLanguage === 'fr' ? 'fr-CA' : 'en-CA', {style: 'currency', currency: 'CAD'});
    private intlMoneyFormat = (price: number) => {
        return this.intlMoney.format(price / 100);
    }

    private displayPrice = () => {
        const price: number = parseInt(this.state.currentVariant.price);
        const compareAtPrice = parseInt(this.state.currentVariant.compare_at_price);
        const productSku: string = this.state.currentVariant.sku.toLowerCase();
        const privilegeCardSkus: Array<string> = ['nv000025', 'nv000026', 'nv000027', 'nv000028'];

        return(
            <>
                <div className={'price-container'}>
                    {compareAtPrice && compareAtPrice > price ?
                        (
                            <>
                                <div className={'compare-at-price'}>
                                    <span>{this.intlMoneyFormat(compareAtPrice)}</span>
                                    <span>{this.currentLanguage === 'fr' ? 'régulier' : 'regular'}</span>
                                </div>
                                <div className={'price'}>
                                    <span>{this.intlMoneyFormat(price)}</span>
                                    <span>{this.currentLanguage === 'fr' ? 'solde' : 'sale'}</span>
                                </div>
                                {this.percentageCalculation(price,  compareAtPrice) >= this.internalData.settings.finalSaleThreshold &&
                                    <div className={'final-sale'}>
                                        <span className={'final-sale__msg'}>{this.languages.productFinalSaleText}</span>
                                    </div>
                                }
                            </>
                        )
                        :
                        (
                            <div className={'price'}>
                                <span>{this.intlMoneyFormat(price)}</span>
                                {this.state.isPrivileged && !privilegeCardSkus.includes(productSku) &&
                                    <span>{this.currentLanguage === 'fr' ? 'régulier' : 'regular'}</span>
                                }
                            </div>
                        )
                    }
                    {this.state.isPrivileged && !privilegeCardSkus.includes(productSku) &&
                        <div className={'privilege'}>
                            <span>{this.intlMoneyFormat(price * (1 - (this.internalData.card.discount / 100)))}</span>
                            <span>{this.internalData.card.name}</span>
                        </div>
                    }
                </div>
                <div className={'cf-avantage'}>
                    {this.state.isPrivileged && !privilegeCardSkus.includes(productSku) &&
                        <>
                            <span>{this.currentLanguage === 'fr' ? 'Votre prix' : 'Your price'} {this.internalData.card.name.toUpperCase()}</span>
                            <span>{this.intlMoneyFormat(price * (1 - (this.internalData.card.discount / 100)))}</span>
                        </>
                    }
                </div>
            </>
        )
    }

    private percentageCalculation = (price: number, compareAtPrice: number) => {
        return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
    }

    private displayPriceBSF = () => {
        const price: number = parseInt(this.state.currentVariant.price);
        const compareAtPrice = parseInt(this.state.currentVariant.compare_at_price);
        const productSku: string = this.state.currentVariant.sku.toLowerCase();
        const privilegeCardSkus: Array<string> = ['nv000025', 'nv000026', 'nv000027', 'nv000028'];

        return (
            <div className={'price-container-bsf'}>
                {compareAtPrice && compareAtPrice > price ?
                    (
                        <>
                            <span className={'compare-at-price'}>{this.intlMoneyFormat(compareAtPrice)}</span>
                            <span className={'price'}><span>-{this.percentageCalculation(price, compareAtPrice)}%</span> <span>{this.intlMoneyFormat(price)}</span></span>
                        </>
                    )
                    :
                    (
                        <span className={'price'}>{this.intlMoneyFormat(price)}</span>
                    )
                }
                {this.state.isPrivileged && !privilegeCardSkus.includes(productSku) &&
                    <div className={'privilege'}>
                        <span>{this.currentLanguage === 'fr' ? 'Prix membre' : 'Member price'} {this.internalData.card.name}</span>
                        <span>{this.intlMoneyFormat(price * (1 - (this.internalData.card.discount / 100)))}</span>
                    </div>
                }
                {this.percentageCalculation(price,  compareAtPrice) >= this.internalData.settings.finalSaleThreshold &&
                    <div className={'final-sale'}>
                        <span className={'final-sale__msg'}>{this.languages.productFinalSaleText}</span>
                    </div>
                }
            </div>
        )
    }

    private changeVariant = (optionPosition: number, value: string) => {
        // @ts-ignore
        this.selectedOptions[`option${optionPosition}`] = value;
        // Find the variant with the selected options
        const newVariant = this.internalData.data.variants.find((variant) => variant.option1 === this.selectedOptions.option1 && variant.option2 === this.selectedOptions.option2);
        if (newVariant) {
            // Update the url with the new variant
            if (this.internalData.settings.enableHistoryState) {
                const url = new URL(window.location.href);
                url.searchParams.set('variant', newVariant.id.toString());
                window.history.replaceState({ }, '', url.href);
            }
            this.setState({currentVariant: newVariant});
        }
    }

    private changeThumbnail = (index: number) => {
        this.setState({currentThumbnail: index});
    }

    private scrollToMedia = (index: number) => {
        const media = document.querySelector(`[data-media-index="${index}"]`) as HTMLElement;
        if (media) {
            media.scrollIntoView({behavior: 'smooth'});
        }
    }

    private addVariantToCart = async (variantId: number) => {
        await fetch("/cart/add.json", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({
                id: variantId,
                quantity: 1
            })
        })
    }

    private handleAddToCart = async () => {
        await this.addVariantToCart(this.state.currentVariant.id);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        this.sidebarCart!.setAttribute('aria-hidden', 'false');
        setTimeout(() => {
            this.sidebarCart!.setAttribute('aria-hidden', 'true');
        }, 3000);
    }

    private computeReturnAfterLoginUrl = () => {
        const currUrl = new URL(window.location.href);
        return `?return_url=${encodeURIComponent(`${currUrl.pathname}?variant=${this.state.currentVariant.id}`)}`;
    }

    private isOption1Available = (option1: string) => {
        //Function that checks if any of the variants with the same option 1 are available
        return this.internalData.data.variants.some((variant) => {
            if (variant.option1 === option1) {
                return variant.available;
            }
            return false;
        });
    }

    private displayBecomePrivilegeCta = () => {
        if (!this.internalData.customer.loggedIn) {
            return (
                <div className={'privilege-cta tw-mt-4 tw-mb-6 md:tw-my-6'}>
                    <span>{this.internalData.translations.profitFromCard}</span>
                    <a href={`/account/login${this.computeReturnAfterLoginUrl()}`}>
                        {this.internalData.translations.signIn}
                    </a>
                </div>
            )
        }

        if (this.internalData.customer.loggedIn && !this.state.isPrivileged && !this.state.cardInCart) {
            const text = this.currentLanguage === 'fr' ?
                `Profiter d'une réduction de ${this.internalData.card.discount}% avec la carte <span class="tw-uppercase">${this.internalData.card.name}</span>` :
                `Add the <span class="tw-uppercase">${this.internalData.card.name}</span> card to get a ${this.internalData.card.discount}% discount on your purchases`;
            return (
                <div className={'privilege-cta'}>
                    <p dangerouslySetInnerHTML={{__html: text}}/>
                    <button className={'tw-underline'} onClick={this.addPrivilegeCardToCart}>Ajouter la carte</button>
                </div>
            )
        }
    }

    private addPrivilegeCardToCart = async () => {
        await this.addVariantToCart(this.internalData.card.variantId);
        this.setState({
            cardInCart: true,
            isPrivileged: true
        });
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        this.sidebarCart!.setAttribute('aria-hidden', 'false');
        setTimeout(() => {
            this.sidebarCart!.setAttribute('aria-hidden', 'true');
        }, 3000);
    }

    private openPolicy = (kind: 'refund' | 'shipping') => {
        this.setState({
            policy: {
                opened: true,
                type: kind
            }
        });
    }


    render() {
        return (
            <>
                {this.internalData.customer.isAdmin && this.state.adminError.length > 0 &&
                    <div className={'admin-errors'}>
                        <div className="errors-container">
                            {this.state.adminError.map((error, index) =>
                                <p key={index} dangerouslySetInnerHTML={{__html: error}}/>
                            )}
                        </div>
                    </div>
                }

                {/*<div className={'policy-overlay'} data-open={this.state.policy.opened}>*/}
                {/*    <div className={'policy-modal'}>*/}
                {/*        {this.state.policy.type &&*/}
                {/*            <div className={'policy-content'} dangerouslySetInnerHTML={{__html: this.internalData.data.policies[this.state.policy.type]}}/>*/}
                {/*        }*/}
                {/*        <button*/}
                {/*            className={'close-modal'}*/}
                {/*            onClick={() => this.setState({*/}
                {/*                policy: {*/}
                {/*                    opened: false,*/}
                {/*                    type: this.state.policy.type*/}
                {/*                }*/}
                {/*            })}>*/}
                {/*            <Close/>*/}
                {/*        </button>*/}
                {/*    </div>*/}
                {/*</div>*/}

                <div className={'title-sku-cf'}>
                    <h2 className={'product-title'}>{this.internalData.data.product.title}</h2>
                    {this.internalData.data.product.vendor !== 'BLANK' && !this.internalData.data.product.byVero &&
                        <p className={'product-vendor'}>{this.internalData.data.product.vendor}</p>
                    }
                    <p className={'sku md:tw-hidden'}>{this.state.currentVariant.sku}</p>
                    {this.internalData.data.product.byVero && <p className={'by-vero'}>Véro par Véronique Cloutier</p>}
                </div>

                <div className={'gallery'}>
                    <div className={'thumbnails'}>
                        {this.defaultVariantPerColor.find((variant) => variant.option1 === this.state.currentVariant.option1)?.variantImages.map((image, index) => {

                            // If it's a swatch image, don't display it in the thumbnails
                            if (this.defaultVariantPerColor.find((variant: IProductVariantResolved) => variant.swatchImage?.src === image.src)) return null;

                            return (
                                <img
                                    key={image.id}
                                    data-viewing={index === this.state.currentThumbnail}
                                    className={'thumbnail'}
                                    src={image.previewSrc}
                                    alt={image.alt}
                                    style={{
                                        aspectRatio: image.aspectRatio,
                                        animationDelay: `${index * 0.1}s`
                                    }}
                                    onClick={() => this.scrollToMedia(index)}
                                />
                            )
                        })}
                    </div>
                    <div className={'pictures'}>
                        {this.defaultVariantPerColor.find((variant) => variant.option1 === this.state.currentVariant.option1)?.variantImages.map((image, index) => {
                            // If it's a swatch image, don't display it in the gallery
                            if (this.defaultVariantPerColor.find((variant: IProductVariantResolved) => variant.swatchImage?.src === image.src)) return null;

                            if (index === 0) return (
                                <LazyImage ref={this.firstPicture} key={image.id}
                                           availableSoon={this.internalData.data.product.availableSoon}
                                           changeThumbnail={this.changeThumbnail} index={index} productMedia={image}/>
                            )

                            return (
                                <LazyImage key={image.id} availableSoon={this.internalData.data.product.availableSoon}
                                           changeThumbnail={this.changeThumbnail} index={index} productMedia={image}/>
                            )
                        })}
                    </div>
                </div>
                <Swiper
                    key={this.state.currentVariant.option1}
                    slidesPerView={1}
                    modules={[Pagination]}
                    pagination={{clickable: true}}
                >
                    {this.defaultVariantPerColor.find((variant) => variant.option1 === this.state.currentVariant.option1)?.variantImages.map((image, index) => {
                        // If it's a swatch image, don't display it in the gallery
                        if (this.defaultVariantPerColor.find((variant: IProductVariantResolved) => variant.swatchImage?.src === image.src)) return null;

                        return (
                            <SwiperSlide>
                                <LazyImage key={image.id} availableSoon={this.internalData.data.product.availableSoon} changeThumbnail={this.changeThumbnail} index={index} productMedia={image}/>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
                <div className={'informations'}>
                    <div className={'bsf-title-price'}>
                        <div className={'title'}>
                            {this.internalData.data.product.byVero && <p className={'by-vero'}>Véro par Véronique Cloutier</p>}
                            <h2 className={'product-title'}>{this.internalData.data.product.title}</h2>
                            {this.internalData.data.product.vendor !== 'BLANK' && !this.internalData.data.product.byVero &&
                                <p className={'product-vendor'}>{this.internalData.data.product.vendor}</p>
                            }
                            <p className={'sku tw-mt-1'}>{this.state.currentVariant.sku}</p>
                        </div>
                        <div className={'price-container-bsf'}>
                            {this.displayPriceBSF()}
                        </div>
                    </div>

                    <div className={'title-sku'}>
                        <p className={'sku md:tw-hidden'}>{this.state.currentVariant.sku}</p>
                        {this.internalData.data.product.byVero &&
                            <p className={'by-vero'}>Véro par Véronique Cloutier</p>}
                        <h2 className={'product-title'}>{this.internalData.data.product.title}</h2>
                        {this.internalData.data.product.vendor !== 'BLANK' && !this.internalData.data.product.byVero &&
                            <p className={'product-vendor'}>{this.internalData.data.product.vendor}</p>
                        }
                    </div>

                    <div className={'tw-flex tw-gap-1 tw-flex-wrap tw-flex-col tw-mt-1 md:tw-mt-2'}>
                        <p className={'sku tw-hidden md:tw-block md:tw-mb-6'}>{this.state.currentVariant.sku}</p>
                        <>{this.displayPrice()}</>
                    </div>

                    {this.internalData.data.options.option1.name !== "Title" &&
                        <div className={'swatches-container tw-my-3 md:tw-my-6'}>
                            <p>{this.currentLanguage === 'fr' ? 'Couleurs' : 'Colors'}</p>
                            <div className={'swatches-list'}>
                                {this.defaultVariantPerColor.map((variant, index) => {
                                    if (!variant.swatchImage) return (
                                        <div
                                            key={index}
                                            className={'swatch'}
                                            data-active={variant.option1 === this.state.currentVariant.option1}
                                            data-tooltip={variant.option1}  
                                            onClick={() => this.changeVariant(1, variant.option1)}
                                            data-enabled={this.isOption1Available(variant.option1)}
                                        >
                                            <div
                                                className={'swatch-inner'}
                                                style={{
                                                    backgroundColor:
                                                        this.currentLanguage === 'fr' ?
                                                            // @ts-ignore
                                                            Colors.fr[variant.option1]
                                                            :
                                                            // @ts-ignore
                                                            Colors.en[variant.option1]
                                                }}
                                            />
                                        </div>
                                    )

                                    return (
                                        <div 
                                            className={'swatch-wrapper'} 
                                            data-tooltip={variant.option1} 
                                            data-enabled={this.isOption1Available(variant.option1)}>
                                            <img
                                                key={index}
                                                className={'swatch'}
                                                data-active={variant.option1 === this.state.currentVariant.option1}
                                                src={this.defaultVariantPerColor[index]?.swatchImage?.swatchSrc}
                                                alt={variant.option1}
                                                onClick={() => this.changeVariant(1, variant.option1)}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    }

                    {this.internalData.data.options.option2 &&
                        <div className={'sizes-and-guide tw-my-4 md:tw-my-6'}>
                    <p>{this.currentLanguage === 'fr' ? 'Tailles' : 'Sizes'}</p>
                    <div className={'sizes-container'} data-enabled={this.isOption1Available(this.state.currentVariant.option1)}>
                        {this.internalData.data.options.option2.values.map((size, index) => {
                            return (
                                <button
                                    key={index}
                                    className={'size'}
                                    data-active={size === this.state.currentVariant.option2}
                                    data-enabled={this.internalData.data.variants.find((variant) => variant.option1 === this.state.currentVariant.option1 && variant.option2 === size)?.available}
                                    onClick={() => this.changeVariant(2, size)}
                                >
                                    {size}
                                </button>
                            )
                        })}
                    </div>
                    <a className={'size-chart-link'} target={'_blank'}
                       href={this.internalData.data.links.sizeChart.url}>
                        {this.currentLanguage === 'fr' ? 'Guide des tailles' : 'Sizes chart'}
                    </a>
                </div>
                }

                <div className={'add-to-cart-container tw-my-3 md:tw-my-6'}>
                    <button
                        className={'add-to-cart ProductForm__AddToCart Button Button--primary'}
                        data-action="add-to-cart"
                        onClick={this.handleAddToCart}
                        disabled={!this.state.currentVariant.available}
                    >
                            <span>
                                {this.state.currentVariant.available ?
                                    this.internalData.translations.addToCart
                                    :
                                    this.currentLanguage === 'fr' ? 'Épuisé' : 'Sold out'
                                }
                            </span>
                    </button>
                </div>

                {!this.state.isPrivileged && this.internalData.data.options.option1.name !== "Title" && this.displayBecomePrivilegeCta()}

                    {/*<div className={'policies-buttons'}>*/}
                    {/*    <button onClick={() => this.openPolicy('refund')}>*/}
                    {/*        <Box/>*/}
                    {/*        {this.currentLanguage === 'fr' ? 'Retour' : 'Return'}*/}
                    {/*    </button>*/}
                    {/*    <button onClick={() => this.openPolicy('shipping')}>*/}
                    {/*        <Truck/>*/}
                    {/*        {this.currentLanguage === 'fr' ? 'Livraison' : 'Shipping'}*/}
                    {/*    </button>*/}
                    {/*</div>*/}

                    <div className={'content-text'}>
                        <div className={'bsf-text'}>
                            {this.internalData.data.product.description &&
                                <div>
                                    <p>Description</p>
                                    <p dangerouslySetInnerHTML={{__html: this.internalData.data.product.description}}/>
                                </div>
                            }
                            {this.internalData.data.product.composition &&
                                <div>
                                    <p>Composition</p>
                                    <p dangerouslySetInnerHTML={{__html: this.internalData.data.product.composition}}/>
                                </div>
                            }
                        </div>

                        <div className={'hide-bsf'}>
                            {this.internalData.data.product.description &&
                                <Drawer title={'Description'} content={this.internalData.data.product.description}/>
                            }
                            {this.internalData.data.product.composition &&
                                <Drawer title={'Composition'} content={this.internalData.data.product.composition}/>
                            }
                        </div>

                        <Drawer title={this.currentLanguage === 'fr' ? 'Retour et expédition' : 'Return and shipping'}
                                content={this.internalData.data.policies.refund + this.internalData.data.policies.shipping}/>
                        {/*<ul className={'additional-infos'}>*/}
                        {/*    {this.internalData.data.product.fit && <li>{this.internalData.data.product.fit}</li>}*/}
                        {/*    {this.internalData.data.product.length && <li>{this.internalData.data.product.length}</li>}*/}
                        {/*    {this.internalData.data.product.shape && <li>{this.internalData.data.product.shape}</li>}*/}
                        {/*</ul>*/}
                    </div>

                    <CFContentText
                        description={this.internalData.data.product.description}
                        composition={this.internalData.data.product.composition}
                        expedition={this.internalData.data.policies.shipping}
                    />

                    {/*<div className="share-box">*/}
                    {/*    <span>{this.currentLanguage === 'fr' ? 'Partager' : 'Share'} : </span>*/}
                    {/*    <div className="share-buttons">*/}
                    {/*        <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noreferrer">*/}
                    {/*            <Facebook/>*/}
                    {/*        </a>*/}
                    {/*        <a href={`https://twitter.com/intent/tweet?url=${window.location.href}`} target="_blank" rel="noreferrer">*/}
                    {/*            <Twitter/>*/}
                    {/*        </a>*/}
                    {/*        <a href={`https://www.pinterest.com/pin/create/button/?url=${window.location.href}`} target="_blank" rel="noreferrer">*/}
                    {/*            <Pinterest/>*/}
                    {/*        </a>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>
            </>
        );
    }
}